import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

// interface
import { IProductService } from '../../../shared/interfaces/product';

// models
import { ApplicationSettings } from '../../../shared/models/application-settings';
import { GridProperty, NonGroupingSelection, Product, ProductClockFace, ProductFunctionKeysAction, ProductFunctionKeysExpression, ProductProperty, ProductPropertyGroup } from '../../../shared/models/product';
import { MasterDataListField } from '../../../shared/models/master-data';
import { SystemProductProperty } from '../../../shared/models/system-product-property';
import { Report } from '../../../shared/models/report';

// services
import { WebApiService } from '../../../shared/services/web-api.service';


@Injectable()
export class ProductService implements IProductService {

  private apiPath: string;

  constructor(private appSettings: ApplicationSettings, private webApiService: WebApiService) {
    this.apiPath = this.appSettings.adminApi + 'auctioncluster';
  }

  getProducts(auctionClusterId: number, langCode: string): Observable<Array<Product>> {
    return this.webApiService.getList(`${this.apiPath}/${auctionClusterId}/product?langCode=${langCode}`);
  }

  getProduct(auctionClusterId: number, productId: number): Observable<Product> {
    return this.webApiService.get(`${this.apiPath}/${auctionClusterId}/product`, productId);
  }

  save(auctionClusterId: number, product: Product): Observable<any> {
    return this.webApiService.save<Product>(`${this.apiPath}/${auctionClusterId}/product`, product);
  }

  edit(auctionClusterId: number, product: Product): Observable<any> {
    return this.webApiService.edit<Product>(`${this.apiPath}/${auctionClusterId}/product`, product.productId, product);
  }

  delete(auctionClusterId: number, productId: number): Observable<any> {
    return this.webApiService.delete(`${this.apiPath}/${auctionClusterId}/product`, productId);
  }

  getSystemProductProperties(): Observable<Array<SystemProductProperty>> {
    return this.webApiService.getList(this.appSettings.adminApi + 'systemproductproperty');
  }

  getReportTypes(): Observable<Array<Report>> {
    return this.webApiService.getList(this.appSettings.adminApi + 'report');
  }

  getNewProductModel(auctionClusterId: number): Observable<Product> {
    return this.webApiService.getSingle(`${this.apiPath}/${auctionClusterId}/product/new`);
  } 
  
  checkExpression(auctionClusterId: number, productId: number, expression: ProductFunctionKeysExpression): Observable<ProductFunctionKeysExpression> {
    return this.webApiService.save<ProductFunctionKeysExpression>(`${this.apiPath}/${auctionClusterId}/product/${productId}/checkExpression`, expression);
  } 

  getProductFunctionKeysActions(auctionClusterId: number, productId: number): Observable<Array<ProductFunctionKeysAction>> {
    return this.webApiService.getSingle(`${this.apiPath}/${auctionClusterId}/product/${productId}/functionkeysaction`);
  }

  saveProductFunctionKeysActions(auctionClusterId: number, productId: number, productFunctionKeysActions: Array<ProductFunctionKeysAction>): Observable<any> {
    return this.webApiService.save<Array<ProductFunctionKeysAction>>(`${this.apiPath}/${auctionClusterId}/product/${productId}/functionkeysaction`, productFunctionKeysActions);
  }

  getProductFunctionKeysExpressions(auctionClusterId: number, productId: number): Observable<Array<ProductFunctionKeysExpression>> {
    return this.webApiService.getSingle(`${this.apiPath}/${auctionClusterId}/product/${productId}/functionkeysexpression`);
  }

  saveProductFunctionKeysExpressions(auctionClusterId: number, productId: number, productFunctionKeysExpressions: Array<ProductFunctionKeysExpression>): Observable<any> {
    return this.webApiService.save<Array<ProductFunctionKeysExpression>>(`${this.apiPath}/${auctionClusterId}/product/${productId}/functionkeysexpression`, productFunctionKeysExpressions);
  }

  getProductClockFaces(auctionClusterId: number, productId: number, type: number): Observable<Array<ProductClockFace>> {
    return this.webApiService.getSingle(`${this.apiPath}/${auctionClusterId}/product/${productId}/clockface/type/${type}`);
  }

  saveProductClockFaces(auctionClusterId: number, productId: number, type: number, productClockFaces: Array<ProductClockFace>): Observable<any> {
    return this.webApiService.save<Array<ProductClockFace>>(`${this.apiPath}/${auctionClusterId}/product/${productId}/clockface/type/${type}`, productClockFaces);
  }

  //IProductService

  getProductForReports(auctionClusterId: number, productId: number)
  {
    return this.webApiService.get(this.apiPath + '/' + auctionClusterId + '/product', productId);
  }

  getProductProperties(productId: number, langCode: string): Observable<Array<ProductProperty>> {
    return this.webApiService.getSingle(`${this.appSettings.adminApi}product/${productId}/productproperties?langCode=${langCode}`);
  }

  getReportProperties(productId: number, reportDesignId: number): Observable<Array<GridProperty>> {
    return this.webApiService.getSingle(`${this.appSettings.adminApi}product/${productId}/reportproperties/${reportDesignId}`);
  }

  getMasterDataListFields(productId: number, productPropertyId: number, langCode: string): Observable<Array<MasterDataListField>> {
    return this.webApiService.getSingle(`${this.appSettings.adminApi}product/${productId}/productproperties/${productPropertyId}/masterdatalistfields?langCode=${langCode}`);
  }

  getGrouping(productId: number, productPropertyId: number,langCode: string): Observable<Array<NonGroupingSelection>> {
    return this.webApiService.getSingle(`${this.appSettings.adminApi}product/${productId}/productproperties/${productPropertyId}/grouping?langCode=${langCode}`);
  }
}
